import { z } from 'zod';
import { ROLE_SCOPES } from './permissions';

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z.string().trim().max(max).optional().nullable().transform((v) => (v ? v : null));
const code = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .regex(/^[A-Za-z0-9._-]+$/, 'Use letters, numbers, dot, dash or underscore')
  .transform((v) => v.toUpperCase());
const money = z.coerce.number().min(0).max(1_000_000_000).multipleOf(0.01);
/** Minimum stock before an item shows on the low stock report; null = none */
const reorderLevel = z.coerce.number().min(0).max(1_000_000_000).multipleOf(0.001).optional().nullable();

/**
 * Amounts are stored in SAR and shown in each user's currency (SAR or CNY,
 * renminbi) at the latest exchange rate. Inputs may be in either currency.
 */
export const CURRENCIES = ['SAR', 'CNY'] as const;
export type Currency = (typeof CURRENCIES)[number];
export const CURRENCY_LABEL: Record<Currency, string> = { SAR: 'SAR', CNY: 'CNY (RMB)' };
/** Currency amounts are stored in */
export const STORAGE_CURRENCY: Currency = 'SAR';
export const DEFAULT_CURRENCY: Currency = 'SAR';
export const currencySchema = z.enum(CURRENCIES);
export const DEFAULT_TIMEZONE = 'Asia/Riyadh';
/** Legacy IANA names that browsers and Node still report, and their current names */
export const TIMEZONE_ALIASES: Readonly<Record<string, string>> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Europe/Kiev': 'Europe/Kyiv',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'Pacific/Truk': 'Pacific/Chuuk',
};
/** The current name of a time zone (Asia/Calcutta becomes Asia/Kolkata) */
export const currentTimezoneName = (tz: string): string => TIMEZONE_ALIASES[tz] ?? tz;
/** IANA time zone name, validated against the runtime's list where available; legacy names are saved under their current name */
export const timezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine((tz) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, 'Unknown time zone')
  .transform(currentTimezoneName);
export const PLATFORMS = ['web', 'android', 'ios'] as const;
export const BRANCH_TYPES = ['CENTRAL_WAREHOUSE', 'BRANCH'] as const;
export const UNITS = ['PCS', 'SET', 'BOX', 'PAIR', 'KG', 'M', 'ROLL', 'PACK'] as const;

export const passwordSchema = z
  .string()
  .min(8, 'At least 8 characters')
  .max(128)
  .regex(/[A-Za-z]/, 'Include a letter')
  .regex(/[0-9]/, 'Include a number');

// ---------- Auth ----------
export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(128),
  deviceName: z.string().trim().max(100).optional(),
  platform: z.enum(PLATFORMS).default('web'),
  appVersion: z.string().trim().max(20).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** Send only what changes; null returns to the application default */
export const updateMyProfileSchema = z.object({ timezone: timezoneSchema.nullable().optional(), currency: currencySchema.nullable().optional() });
export type UpdateMyProfileInput = z.infer<typeof updateMyProfileSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ---------- Branches ----------
export const branchCreateSchema = z.object({
  code,
  name: trimmed(120),
  type: z.enum(BRANCH_TYPES),
  address: optionalText(500),
  phone: optionalText(30),
  email: z.string().trim().toLowerCase().email().optional().nullable().or(z.literal('').transform(() => null)),
  managerUserId: z.string().uuid().optional().nullable(),
});
export type BranchCreateInput = z.infer<typeof branchCreateSchema>;
export const branchUpdateSchema = branchCreateSchema.partial().extend({ version: z.number().int() });
export type BranchUpdateInput = z.infer<typeof branchUpdateSchema>;

// ---------- Roles ----------
export const roleCreateSchema = z.object({
  code,
  name: trimmed(80),
  scope: z.enum(ROLE_SCOPES),
  description: optionalText(300),
});
export type RoleCreateInput = z.infer<typeof roleCreateSchema>;
export const roleUpdateSchema = z.object({
  name: trimmed(80).optional(),
  description: optionalText(300),
  version: z.number().int(),
});
export type RoleUpdateInput = z.infer<typeof roleUpdateSchema>;
export const rolePermissionsSchema = z.object({
  permissions: z.array(z.string()).max(200),
  version: z.number().int(),
});
export type RolePermissionsInput = z.infer<typeof rolePermissionsSchema>;

// ---------- Users ----------
export const userCreateSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  fullName: trimmed(120),
  phone: optionalText(30),
  roleId: z.string().uuid(),
  branchIds: z.array(z.string().uuid()).max(100).default([]),
  primaryBranchId: z.string().uuid().optional().nullable(),
  timezone: timezoneSchema.optional().nullable(),
  currency: currencySchema.optional().nullable(),
  /** Supplier logins only (roles scoped to suppliers): the one supplier this user works for. Empty for the China team */
  supplierId: z.string().uuid().optional().nullable(),
  temporaryPassword: passwordSchema,
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;
export const userUpdateSchema = userCreateSchema
  .omit({ temporaryPassword: true, email: true })
  .partial()
  .extend({ email: z.string().trim().toLowerCase().email().optional(), version: z.number().int() });
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;
export const resetPasswordSchema = z.object({ temporaryPassword: passwordSchema });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ---------- Suppliers & categories ----------
export const supplierCreateSchema = z.object({
  code,
  name: trimmed(160),
  country: optionalText(60),
  contactName: optionalText(120),
  phone: optionalText(30),
  email: z.string().trim().toLowerCase().email().optional().nullable().or(z.literal('').transform(() => null)),
  /** Code printed on this supplier's cartons (JY); carton numbers on a ship are JY-001, JY-002 … */
  shippingMark: z
    .string()
    .trim()
    .max(12)
    .optional()
    .nullable()
    .transform((v) => (v === undefined ? undefined : v ? v.toUpperCase().replace(/\s+/g, '') : null))
    .refine((v) => !v || /^[A-Z0-9][A-Z0-9-]{0,11}$/.test(v), 'Letters, digits and dashes only, up to 12 characters'),
});
export type SupplierCreateInput = z.infer<typeof supplierCreateSchema>;
export const supplierUpdateSchema = supplierCreateSchema.partial().extend({ version: z.number().int() });
export type SupplierUpdateInput = z.infer<typeof supplierUpdateSchema>;

export const categoryCreateSchema = z.object({
  name: trimmed(120),
  parentId: z.string().uuid().optional().nullable(),
  /** Applies to items in the category that have no reorder level of their own */
  reorderLevel,
});
export type CategoryCreateInput = z.infer<typeof categoryCreateSchema>;
export const categoryUpdateSchema = categoryCreateSchema.partial().extend({ version: z.number().int() });
export type CategoryUpdateInput = z.infer<typeof categoryUpdateSchema>;

// ---------- Items ----------
const packQty = z.coerce.number().positive().max(1_000_000).multipleOf(0.001).optional().nullable();
const cm = z.coerce.number().positive().max(10_000).multipleOf(0.01).optional().nullable();
const kg = z.coerce.number().min(0).max(100_000).multipleOf(0.001).optional().nullable();
/** How the item is packed: inner packs per carton and the carton's size and weight */
const packingFields = {
  pcsPerInner: packQty,
  innersPerCarton: packQty,
  cartonLengthCm: cm,
  cartonWidthCm: cm,
  cartonHeightCm: cm,
  cartonGrossKg: kg,
  cartonNetKg: kg,
};
/** Packing of one item by one supplier; every field left empty falls back to the item's own packing */
export const itemPackingSchema = z.object({
  supplierId: z.string().uuid(),
  pcsPerCarton: packQty,
  ...packingFields,
  note: optionalText(500),
});
export type ItemPackingInput = z.infer<typeof itemPackingSchema>;

export const itemCreateSchema = z.object({
  sku: z.string().trim().min(1).max(64).transform((v) => v.toUpperCase()),
  barcode: optionalText(64),
  name: trimmed(200),
  description: optionalText(2000),
  categoryId: z.string().uuid().optional().nullable(),
  supplierId: z.string().uuid().optional().nullable(),
  unit: z.enum(UNITS).default('PCS'),
  price: money,
  /** Currency the amounts are entered in; defaults to the user's currency. Stored as SAR. */
  priceCurrency: z.enum(CURRENCIES).optional(),
  cost: money.optional().nullable(),
  costCurrency: z.enum(CURRENCIES).optional().nullable(),
  requiresSerials: z.boolean().default(false),
  /** Overrides the category's reorder level; null falls back to it */
  reorderLevel,
  /** Units in one carton, used to check supplier invoices and to count cartons on orders */
  pcsPerCarton: z.coerce.number().positive().max(1_000_000).multipleOf(0.001).optional().nullable(),
  ...packingFields,
});
export type ItemCreateInput = z.infer<typeof itemCreateSchema>;
export const itemUpdateSchema = itemCreateSchema.partial().extend({ version: z.number().int() });
export type ItemUpdateInput = z.infer<typeof itemUpdateSchema>;

export const itemImageAttachSchema = z.object({ uploadKey: z.string().min(1).max(300) });
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
] as const;
export const presignUploadSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  contentType: z.enum([...IMAGE_TYPES, ...DOCUMENT_TYPES]),
  size: z.number().int().positive().max(20 * 1024 * 1024),
});
export type PresignUploadInput = z.infer<typeof presignUploadSchema>;

/**
 * Files too big for one request (the 77 MB warehouse sheet) go up in parts of
 * at most PART_UPLOAD_SIZE bytes, in order, then the upload id is passed on.
 */
export const PART_UPLOAD_SIZE = 8 * 1024 * 1024;
export const PART_UPLOAD_MAX = 300 * 1024 * 1024;
export const partUploadStartSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  size: z.number().int().positive().max(PART_UPLOAD_MAX, 'Files up to 300 MB can be uploaded'),
});
export type PartUploadStartInput = z.infer<typeof partUploadStartSchema>;

// ---------- Common ----------
export const activeToggleSchema = z.object({ isActive: z.boolean(), version: z.number().int() });
export type ActiveToggleInput = z.infer<typeof activeToggleSchema>;

export const listQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  active: z.enum(['true', 'false', 'all']).default('true'),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

export const itemListQuerySchema = listQuerySchema.extend({
  categoryId: z.string().uuid().optional(),
  supplierId: z.string().uuid().optional(),
});
export type ItemListQuery = z.infer<typeof itemListQuerySchema>;

export const stockQuerySchema = itemListQuerySchema.extend({
  branchId: z.string().uuid().optional(),
  inStockOnly: z.enum(['true', 'false']).default('false'),
  /** Items with no stock at `branchId`, or at any location when it's left out */
  outOfStockOnly: z.enum(['true', 'false']).default('false'),
  /** Column to sort by; `branch` sorts by the quantity at `sortBranchId`. `cost` needs items.cost.view. */
  sort: z.enum(['name', 'price', 'cost', 'total', 'branch']).default('name'),
  sortBranchId: z.string().uuid().optional(),
  dir: z.enum(['asc', 'desc']).default('asc'),
});
export type StockQuery = z.infer<typeof stockQuerySchema>;

export const auditQuerySchema = z.object({
  tableName: z.string().max(64).optional(),
  recordId: z.string().max(64).optional(),
  actorUserId: z.string().uuid().optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

// ---------- China orders (Phase 2) ----------
export const CHINA_ORDER_STATUSES = [
  'DRAFT', 'SUBMITTED', 'SUPPLIER_CONFIRMED', 'PARTIALLY_FULFILLED', 'PACKED', 'PARTLY_SHIPPED', 'SHIPPED', 'RECEIVED', 'REJECTED', 'CANCELLED',
] as const;
export type ChinaOrderStatus = (typeof CHINA_ORDER_STATUSES)[number];
export const ATTACHMENT_KINDS = ['PHOTO', 'PACKING_LIST', 'INVOICE', 'OTHER'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];
/** The supplier's progress on a line before it ships, as the warehouse sheet records it */
export const LINE_SUPPLIER_STATUSES = ['CHECKING', 'PRICE_HIGH', 'IN_PRODUCTION', 'READY'] as const;
export type LineSupplierStatus = (typeof LINE_SUPPLIER_STATUSES)[number];
export const SHIPMENT_STATUSES = ['IN_TRANSIT', 'ARRIVED', 'RECEIVED', 'CANCELLED'] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

const qty = z.coerce.number().positive().max(1_000_000_000).multipleOf(0.001);
/** Requested order quantities are whole units */
const wholeQty = z.coerce.number().int('Enter a whole number').positive('Enter a quantity of 1 or more').max(1_000_000_000);
const qtyOrZero = z.coerce.number().min(0).max(1_000_000_000).multipleOf(0.001);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-07-12');
const monthDate = z.string().regex(/^\d{4}-\d{2}-01$/, 'Use the first day of a month, e.g. 2026-11-01');

export const chinaOrderLineCreateSchema = z.object({
  itemId: z.string().uuid(),
  targetBranchId: z.string().uuid().optional().nullable(),
  qtyRequested: wholeQty,
  notes: optionalText(500),
  /** Ordered for this shop order line (declined or arrived short); the line goes to that shop */
  branchOrderLineId: z.string().uuid().optional().nullable(),
});
export type ChinaOrderLineCreateInput = z.infer<typeof chinaOrderLineCreateSchema>;

export const chinaOrderCreateSchema = z.object({
  supplierId: z.string().uuid(),
  cycleMonth: monthDate.optional(),
  notes: optionalText(2000),
  /** Lines to add with the draft, in one transaction (the New order screen sends these) */
  lines: z.array(chinaOrderLineCreateSchema).max(500).optional(),
});
export type ChinaOrderCreateInput = z.infer<typeof chinaOrderCreateSchema>;

export const chinaOrderUpdateSchema = z.object({
  cycleMonth: monthDate.optional(),
  notes: optionalText(2000),
  version: z.number().int(),
});
export type ChinaOrderUpdateInput = z.infer<typeof chinaOrderUpdateSchema>;

export const chinaOrderLineUpdateSchema = z.object({
  targetBranchId: z.string().uuid().optional().nullable(),
  qtyRequested: wholeQty.optional(),
  notes: optionalText(500),
  version: z.number().int(),
});
export type ChinaOrderLineUpdateInput = z.infer<typeof chinaOrderLineUpdateSchema>;

/** What the China team may change on a line (FR-3.3): confirmed quantity and supplier price */
export const supplierLineEditSchema = z.object({
  qtyConfirmed: qtyOrZero,
  unitCost: money.optional().nullable(),
  costCurrency: z.enum(CURRENCIES).optional().nullable(),
  supplierNote: optionalText(500),
  version: z.number().int(),
});
export type SupplierLineEditInput = z.infer<typeof supplierLineEditSchema>;

export const serialsSchema = z.object({
  serials: z.array(z.string().trim().min(1).max(100)).max(10000),
});
export type SerialsInput = z.infer<typeof serialsSchema>;

/** Supplier progress on a line (China team or purchase manager): "checking", "ready by 5 Nov" */
export const lineProgressSchema = z.object({
  supplierStatus: z.enum(LINE_SUPPLIER_STATUSES).nullable(),
  readyOn: isoDate.optional().nullable(),
  supplierNote: optionalText(500),
  version: z.number().int(),
});
export type LineProgressInput = z.infer<typeof lineProgressSchema>;

export const statusReasonSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  version: z.number().int(),
});
export type StatusReasonInput = z.infer<typeof statusReasonSchema>;

export const versionOnlySchema = z.object({ version: z.number().int(), note: optionalText(2000) });
export type VersionOnlyInput = z.infer<typeof versionOnlySchema>;

/** Mark a shipped or partly shipped order received from the orders page */
export const chinaOrderReceiveSchema = versionOnlySchema.extend({
  /**
   * Also add to central warehouse stock the goods of this order that arrived
   * before I-GOAL (counted as received by the sheet import, with no stock added)
   */
  addArrivedToStock: z.boolean().optional(),
});
export type ChinaOrderReceiveInput = z.infer<typeof chinaOrderReceiveSchema>;

export const attachmentCreateSchema = z.object({
  uploadKey: z.string().min(1).max(300),
  kind: z.enum(ATTACHMENT_KINDS),
  lineId: z.string().uuid().optional().nullable(),
  fileName: z.string().trim().min(1).max(200),
});
export type AttachmentCreateInput = z.infer<typeof attachmentCreateSchema>;

export const chinaOrderListQuerySchema = z.object({
  status: z.enum(CHINA_ORDER_STATUSES).optional(),
  open: z.enum(['true', 'false']).optional(),
  supplierId: z.string().uuid().optional(),
  cycleMonth: monthDate.optional(),
  search: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ChinaOrderListQuery = z.infer<typeof chinaOrderListQuerySchema>;

export const notificationListQuerySchema = z.object({
  unread: z.enum(['true', 'false']).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;

// ---------- Reports (Phase 5, FR-9) ----------
export const MOVEMENT_TYPES = ['OPENING', 'RECEIPT', 'TRANSFER_OUT', 'TRANSFER_IN', 'ADJUSTMENT', 'SALE'] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];
/** Movements that count as stock going out for the slow stock report. Adjustments are corrections, not demand. */
export const OUTWARD_MOVEMENT_TYPES: MovementType[] = ['TRANSFER_OUT', 'SALE'];
export const SLOW_STOCK_DAYS = [60, 90, 180] as const;


/** Display stock for one item at one location (stock.display.manage). null shows actual stock again. */
export const displayStockSchema = z.object({
  itemId: z.string().uuid(),
  branchId: z.string().uuid(),
  displayQuantity: qtyOrZero.nullable(),
  version: z.number().int(),
});
export type DisplayStockInput = z.infer<typeof displayStockSchema>;

/** Undo the most recent opening stock import; `version` comes from the history list */
export const stockImportRevertSchema = z.object({ version: z.number().int() });
export type StockImportRevertInput = z.infer<typeof stockImportRevertSchema>;

const reportItemFilters = {
  search: z.string().trim().max(100).optional(),
  categoryId: z.string().uuid().optional(),
  supplierId: z.string().uuid().optional(),
};
const reportPaging = {
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
};

export const stockByLocationQuerySchema = z.object({
  ...reportItemFilters,
  ...reportPaging,
  branchId: z.string().uuid().optional(),
  /** Leave out items with no stock at the locations shown */
  hideZero: z.enum(['true', 'false']).default('true'),
});
export type StockByLocationQuery = z.infer<typeof stockByLocationQuerySchema>;

export const valuationQuerySchema = z.object({
  ...reportItemFilters,
  branchId: z.string().uuid().optional(),
});
export type ValuationQuery = z.infer<typeof valuationQuerySchema>;

export const lowStockQuerySchema = z.object({
  ...reportItemFilters,
  ...reportPaging,
  branchId: z.string().uuid().optional(),
  /** Only items still below their minimum after open orders and shipments arrive */
  afterPipeline: z.enum(['true', 'false']).default('false'),
});
export type LowStockQuery = z.infer<typeof lowStockQuerySchema>;

export const slowStockQuerySchema = z.object({
  ...reportItemFilters,
  ...reportPaging,
  /** Defaults to the central warehouse, or the user's primary branch for branch roles */
  branchId: z.string().uuid().optional(),
  days: z.coerce.number().int().min(1).max(3650).default(90),
});
export type SlowStockQuery = z.infer<typeof slowStockQuerySchema>;

/** Filters shared by the movement list, its export and its totals */
const movementFilters = {
  /** Calendar dates, inclusive, in the user's time zone */
  from: isoDate.optional(),
  to: isoDate.optional(),
  itemId: z.string().uuid().optional(),
  branchId: z.string().uuid().optional(),
  type: z.enum(MOVEMENT_TYPES).optional(),
  /** Item name or SKU, or a reference such as an order number */
  search: z.string().trim().max(100).optional(),
};
const datesInOrder = (q: { from?: string; to?: string }) => !q.from || !q.to || q.from <= q.to;
const datesInOrderError = { path: ['to'], message: 'The end date is before the start date' };

export const movementReportQuerySchema = z.object({ ...reportPaging, ...movementFilters }).refine(datesInOrder, datesInOrderError);
export type MovementReportQuery = z.infer<typeof movementReportQuerySchema>;

/**
 * Stock in and out per day (per week over 62 days, per month over a year), for the movements chart.
 * Without dates: the last 30 days up to today. At most 5 years.
 */
export const movementTotalsQuerySchema = z
  .object(movementFilters)
  .refine(datesInOrder, datesInOrderError)
  .refine((q) => !q.from || !q.to || (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 5 * 366, {
    path: ['from'],
    message: 'Choose a range of five years or less',
  });
export type MovementTotalsQuery = z.infer<typeof movementTotalsQuerySchema>;
/** All order lines across orders: the warehouse's order sheet */
export const orderLineListQuerySchema = z.object({
  supplierId: z.string().uuid().optional(),
  itemId: z.string().uuid().optional(),
  /** true: still waiting for goods (default); false: closed; all */
  open: z.enum(['true', 'false', 'all']).default('true'),
  /** true: only lines that can go on a ship now (confirmed order, quantity left to ship) */
  toShip: z.enum(['true']).optional(),
  search: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type OrderLineListQuery = z.infer<typeof orderLineListQuerySchema>;

// ---------- Shipments and supplier invoices ----------
const shipNo = z.string().trim().min(1).max(40).transform((v) => v.toUpperCase());
const containerNo = z
  .string()
  .trim()
  .max(20)
  .optional()
  .nullable()
  // undefined stays undefined (leave as is); empty clears it
  .transform((v) => (v === undefined ? undefined : v ? v.replace(/\s+/g, '').toUpperCase() : null));
const invoiceNo = z.string().trim().min(1).max(40).transform((v) => v.toUpperCase());
const charge = money.default(0);

export const shipmentCreateSchema = z.object({
  shipNo,
  containerNo,
  shippedOn: isoDate.optional().nullable(),
  etaDate: isoDate.optional().nullable(),
  cartons: z.coerce.number().int().min(0).max(1_000_000).optional().nullable(),
  lastUpdate: optionalText(500),
  /** Where the goods go: a branch, or null for the central warehouse */
  destinationBranchId: z.string().uuid().optional().nullable(),
});
export type ShipmentCreateInput = z.infer<typeof shipmentCreateSchema>;

export const shipmentUpdateSchema = z.object({
  shipNo: shipNo.optional(),
  containerNo,
  shippedOn: isoDate.optional().nullable(),
  etaDate: isoDate.optional().nullable(),
  cartons: z.coerce.number().int().min(0).max(1_000_000).optional().nullable(),
  lastUpdate: optionalText(500),
  destinationBranchId: z.string().uuid().optional().nullable(),
  version: z.number().int(),
});
export type ShipmentUpdateInput = z.infer<typeof shipmentUpdateSchema>;

export const shipmentArriveSchema = z.object({ arrivedOn: isoDate.optional().nullable(), version: z.number().int() });
export type ShipmentArriveInput = z.infer<typeof shipmentArriveSchema>;

export const shipmentInvoiceCreateSchema = z.object({
  invoiceNo,
  supplierId: z.string().uuid(),
  invoiceDate: isoDate.optional().nullable(),
  cartons: z.coerce.number().int().min(0).max(1_000_000).optional().nullable(),
  freight: charge,
  telex: charge,
  docFee: charge,
  insurance: charge,
  otherCharges: charge,
  /** Saudi customs duty in the invoice currency; counts in landed cost once entered */
  customsDuty: charge,
  note: optionalText(1000),
});
export type ShipmentInvoiceCreateInput = z.infer<typeof shipmentInvoiceCreateSchema>;

export const shipmentInvoiceUpdateSchema = z.object({
  invoiceNo: invoiceNo.optional(),
  invoiceDate: isoDate.optional().nullable(),
  cartons: z.coerce.number().int().min(0).max(1_000_000).optional().nullable(),
  freight: money.optional(),
  telex: money.optional(),
  docFee: money.optional(),
  insurance: money.optional(),
  otherCharges: money.optional(),
  customsDuty: money.optional(),
  note: optionalText(1000),
  version: z.number().int(),
});
export type ShipmentInvoiceUpdateInput = z.infer<typeof shipmentInvoiceUpdateSchema>;

export const shipmentLineCreateSchema = z.object({
  invoiceId: z.string().uuid().optional().nullable(),
  /** null for goods that aren't an item in the catalogue (e.g. free spare parts) */
  itemId: z.string().uuid().nullable(),
  description: optionalText(200),
  model: optionalText(100),
  cartonRange: optionalText(40),
  /** Defaults to the invoice's supplier, then the item's */
  supplierId: z.string().uuid().optional().nullable(),
  cartonFrom: z.coerce.number().int().positive().max(1_000_000).optional().nullable(),
  cartonTo: z.coerce.number().int().positive().max(1_000_000).optional().nullable(),
  pcsPerCarton: qty.optional().nullable(),
  cartons: qtyOrZero.optional().nullable(),
  qty: qtyOrZero,
  unitPrice: money.optional().nullable(),
});
export type ShipmentLineCreateInput = z.infer<typeof shipmentLineCreateSchema>;

export const shipmentLineUpdateSchema = shipmentLineCreateSchema.omit({ invoiceId: true }).partial().extend({ version: z.number().int() });
export type ShipmentLineUpdateInput = z.infer<typeof shipmentLineUpdateSchema>;

export const shipmentReceiveSchema = z.object({
  version: z.number().int(),
  /** Lines left out are taken as received in full */
  lines: z.array(z.object({ lineId: z.string().uuid(), qtyReceived: qtyOrZero })).default([]),
  note: optionalText(2000),
});
export type ShipmentReceiveInput = z.infer<typeof shipmentReceiveSchema>;

/**
 * Ship confirmed order lines without an invoice file: the China team picks the
 * lines and quantities on the orders page. The ship no and invoice no are
 * found or created; charges can be added on the shipment afterwards.
 */
/**
 * Invoice charges line by line. The kind decides which total it adds to on the
 * invoice (freight, telex, doc fee, insurance, other); the particular is the
 * wording, e.g. "Sea freight 40HQ" or "Loading charge".
 */
export const INVOICE_CHARGE_KINDS = ['FREIGHT', 'TELEX', 'DOC_FEE', 'INSURANCE', 'OTHER'] as const;
export type InvoiceChargeKind = (typeof INVOICE_CHARGE_KINDS)[number];
export const INVOICE_CHARGE_LABEL: Record<InvoiceChargeKind, string> = {
  FREIGHT: 'Freight / shipping',
  TELEX: 'Telex release',
  DOC_FEE: 'Document fee',
  INSURANCE: 'Insurance',
  OTHER: 'Other charges',
};
export const invoiceChargeSchema = z.object({
  kind: z.enum(INVOICE_CHARGE_KINDS),
  /** Left empty: the kind's label */
  particular: z.string().trim().max(120).optional().nullable(),
  amount: money.refine((v) => v > 0, 'Enter an amount above 0'),
});
export type InvoiceChargeInput = z.infer<typeof invoiceChargeSchema>;

/** DELETE with the version the user saw; refused with STALE_VERSION when it changed since */
export const versionQuerySchema = z.object({ version: z.coerce.number().int().optional() });
export type VersionQuery = z.infer<typeof versionQuerySchema>;

export const shipmentFromOrdersSchema = z.object({
  shipNo,
  containerNo,
  shippedOn: isoDate.optional().nullable(),
  etaDate: isoDate.optional().nullable(),
  invoiceNo,
  invoiceDate: isoDate.optional().nullable(),
  lines: z
    .array(z.object({ orderLineId: z.string().uuid(), qty }))
    .min(1, 'Choose at least one line to ship')
    .max(500)
    .refine((l) => new Set(l.map((x) => x.orderLineId)).size === l.length, 'A line is listed twice'),
  /** Freight and other charges, in the invoice currency (RMB); added to the invoice */
  charges: z.array(invoiceChargeSchema).max(50).default([]),
});
export type ShipmentFromOrdersInput = z.infer<typeof shipmentFromOrdersSchema>;

/** Number the cartons of a shipment: mark-001 … per supplier, in line order */
export const shipmentNumberCartonsSchema = z.object({
  version: z.number().int(),
  /** Only this supplier's lines; all suppliers when left out */
  supplierId: z.string().uuid().optional(),
  /** First number to use (default 1) */
  startAt: z.coerce.number().int().positive().max(1_000_000).default(1),
});
export type ShipmentNumberCartonsInput = z.infer<typeof shipmentNumberCartonsSchema>;

export const shipmentListQuerySchema = z.object({
  status: z.enum(SHIPMENT_STATUSES).optional(),
  /** Shipments going to this branch (the central warehouse's id includes shipments with no destination set) */
  destinationBranchId: z.string().uuid().optional(),
  supplierId: z.string().uuid().optional(),
  /** true: in transit or arrived, not yet received */
  open: z.enum(['true', 'false']).optional(),
  search: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ShipmentListQuery = z.infer<typeof shipmentListQuerySchema>;

/**
 * Sent with an invoice upload (multipart field "options", JSON). Every field
 * overrides what was read from the file; matches maps an invoice line number
 * (as in the preview) to an item, or null to leave it unmatched.
 */
// ---------- Invoice register and stock transfers (Phase 4) ----------
export const INVOICE_CHECK_STATES = ['UNCHECKED', 'CHECKED'] as const;
export const invoiceListQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  supplierId: z.string().uuid().optional(),
  checked: z.enum(['true', 'false']).optional(),
  /** received = the shipment is received; open = not yet */
  shipment: z.enum(['open', 'received']).optional(),
  /** true: some received goods not yet sent on to a branch */
  toSend: z.enum(['true', 'false']).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type InvoiceListQuery = z.infer<typeof invoiceListQuerySchema>;

export const invoiceCheckSchema = z.object({ checked: z.boolean(), version: z.number().int() });
export type InvoiceCheckInput = z.infer<typeof invoiceCheckSchema>;

export const TRANSFER_STATUSES = ['DISPATCHED', 'RECEIVED', 'CANCELLED'] as const;
export type TransferStatus = (typeof TRANSFER_STATUSES)[number];

/** Carton numbers written on the delivery note, e.g. "1-5" or "JY-001 – JY-010"; empty: numbered for you */
const cartonNos = z.string().trim().max(60).optional().nullable();
const transferLine = z.object({ itemId: z.string().uuid(), qty, cartons: qtyOrZero.optional().nullable(), cartonNos });

/** Send stock from one location to another; the receiver acknowledges it */
export const transferCreateSchema = z.object({
  fromBranchId: z.string().uuid().optional(),
  toBranchId: z.string().uuid(),
  lines: z
    .array(transferLine)
    .min(1, 'Add at least one item')
    .max(500)
    .refine((l) => new Set(l.map((x) => x.itemId)).size === l.length, 'An item is listed twice'),
  carrier: optionalText(120),
  note: optionalText(1000),
});
export type TransferCreateInput = z.infer<typeof transferCreateSchema>;

/**
 * Send a received invoice's goods on from the central warehouse to a branch.
 * Lines left out are sent in full (what was received, less what was sent
 * already); a line given 0 stays behind.
 */
export const invoiceTransferSchema = z.object({
  toBranchId: z.string().uuid(),
  lines: z.array(z.object({ lineId: z.string().uuid(), qty: qtyOrZero, cartons: qtyOrZero.optional().nullable(), cartonNos })).max(500).default([]),
  carrier: optionalText(120),
  note: optionalText(1000),
});
export type InvoiceTransferInput = z.infer<typeof invoiceTransferSchema>;

export const transferReceiveSchema = z.object({
  version: z.number().int(),
  /** Lines left out are received as sent */
  lines: z.array(z.object({ lineId: z.string().uuid(), qtyReceived: qtyOrZero })).default([]),
  note: optionalText(1000),
});
export type TransferReceiveInput = z.infer<typeof transferReceiveSchema>;

export const transferListQuerySchema = z.object({
  status: z.enum(TRANSFER_STATUSES).optional(),
  /** true: dispatched, not yet acknowledged */
  open: z.enum(['true', 'false']).optional(),
  branchId: z.string().uuid().optional(),
  invoiceId: z.string().uuid().optional(),
  search: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TransferListQuery = z.infer<typeof transferListQuerySchema>;

// ---------- Shop orders (Phase 5) ----------
/**
 * DRAFT → PLACED (suppliers told) → PARTLY_SENT → SENT (all sent or declined,
 * deliveries not all acknowledged) → DELIVERED. CANCELLED before anything is
 * sent, or when the warehouse declines everything.
 */
export const BRANCH_ORDER_STATUSES = ['DRAFT', 'PLACED', 'PARTLY_SENT', 'SENT', 'DELIVERED', 'CANCELLED'] as const;
export type BranchOrderStatus = (typeof BRANCH_ORDER_STATUSES)[number];
/** Waiting on the warehouse */
export const BRANCH_ORDER_OPEN_STATUSES: BranchOrderStatus[] = ['PLACED', 'PARTLY_SENT'];

const branchOrderLines = z
  .array(z.object({ itemId: z.string().uuid(), qty, note: optionalText(500) }))
  .max(500)
  .refine((l) => new Set(l.map((x) => x.itemId)).size === l.length, 'An item is listed twice');

/** A shop starts an order; it stays a draft until placed */
export const branchOrderCreateSchema = z.object({
  /** The shop ordering; may be left out when the user works at one branch only */
  branchId: z.string().uuid().optional(),
  neededBy: isoDate.optional().nullable(),
  note: optionalText(1000),
  lines: branchOrderLines.default([]),
  /** Place it at once instead of keeping a draft */
  place: z.boolean().default(false),
});
export type BranchOrderCreateInput = z.infer<typeof branchOrderCreateSchema>;
/** What a client sends (optional fields may be left out) */
export type BranchOrderCreateBody = z.input<typeof branchOrderCreateSchema>;

/** Edit a draft; lines, when given, replace all of the draft's lines */
export const branchOrderUpdateSchema = z.object({
  neededBy: isoDate.optional().nullable(),
  note: optionalText(1000),
  lines: branchOrderLines.optional(),
  version: z.number().int(),
});
export type BranchOrderUpdateInput = z.infer<typeof branchOrderUpdateSchema>;
export type BranchOrderUpdateBody = z.input<typeof branchOrderUpdateSchema>;

/**
 * The warehouse sends goods for a shop order as a transfer. Lines left out
 * are sent in full (what is still to send); a line given 0 stays behind.
 */
export const branchOrderSendSchema = z.object({
  version: z.number().int(),
  /** Where the goods leave from; the central warehouse when left out */
  fromBranchId: z.string().uuid().optional(),
  lines: z.array(z.object({ lineId: z.string().uuid(), qty: qtyOrZero, cartons: qtyOrZero.optional().nullable(), cartonNos })).max(500).default([]),
  carrier: optionalText(120),
  note: optionalText(1000),
});
export type BranchOrderSendInput = z.infer<typeof branchOrderSendSchema>;
export type BranchOrderSendBody = z.input<typeof branchOrderSendSchema>;

/** The warehouse says it won't send some of what was ordered */
export const branchOrderDeclineSchema = z.object({
  version: z.number().int(),
  lines: z.array(z.object({ lineId: z.string().uuid(), qty })).min(1, 'Choose what to decline').max(500),
  reason: z.string().trim().min(3, 'Say why, so the shop knows').max(500),
});
export type BranchOrderDeclineInput = z.infer<typeof branchOrderDeclineSchema>;

export const branchOrderListQuerySchema = z.object({
  status: z.enum(BRANCH_ORDER_STATUSES).optional(),
  /** true: placed and waiting on the warehouse; false: everything else except drafts */
  open: z.enum(['true', 'false']).optional(),
  /** true: open and past the needed-by date */
  overdue: z.enum(['true']).optional(),
  branchId: z.string().uuid().optional(),
  search: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type BranchOrderListQuery = z.infer<typeof branchOrderListQuerySchema>;

// ---------- Stock adjustments and counts (Phase 7) ----------
export const ADJUSTMENT_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export type AdjustmentStatus = (typeof ADJUSTMENT_STATUSES)[number];
export const ADJUSTMENT_REASONS = ['COUNT', 'DAMAGED', 'LOST', 'FOUND', 'SAMPLE', 'EXPIRED', 'CORRECTION', 'OTHER'] as const;
export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];
export const ADJUSTMENT_REASON_LABEL: Record<AdjustmentReason, string> = {
  COUNT: 'Stock count',
  DAMAGED: 'Damaged',
  LOST: 'Lost or stolen',
  FOUND: 'Found',
  SAMPLE: 'Given as a sample',
  EXPIRED: 'Expired',
  CORRECTION: 'Correcting a mistake',
  OTHER: 'Other',
};

const signedQty = z.coerce
  .number()
  .min(-1_000_000_000)
  .max(1_000_000_000)
  .multipleOf(0.001)
  .refine((n) => n !== 0, 'Enter how much to add (+) or take off (−)');

/** Typed-in changes at one location: + found, − damaged/lost/given away */
export const adjustmentCreateSchema = z.object({
  /** May be left out by a user of one branch */
  branchId: z.string().uuid().optional(),
  reason: z.enum(ADJUSTMENT_REASONS).refine((r) => r !== 'COUNT', 'Upload a count sheet for a stock count'),
  note: optionalText(1000),
  lines: z
    .array(z.object({ itemId: z.string().uuid(), change: signedQty, note: optionalText(500) }))
    .min(1, 'Add at least one item')
    .max(500)
    .refine((l) => new Set(l.map((x) => x.itemId)).size === l.length, 'An item is listed twice'),
});
export type AdjustmentCreateInput = z.infer<typeof adjustmentCreateSchema>;
export type AdjustmentCreateBody = z.input<typeof adjustmentCreateSchema>;

export const adjustmentDecideSchema = z.object({ version: z.number().int(), note: optionalText(500) });
export type AdjustmentDecideInput = z.infer<typeof adjustmentDecideSchema>;

export const countImportQuerySchema = z.object({
  branchId: z.string().uuid(),
  mode: z.enum(['validate', 'commit']).default('validate'),
  note: optionalText(1000),
});
export type CountImportQuery = z.infer<typeof countImportQuerySchema>;

export const countSheetQuerySchema = z.object({ branchId: z.string().uuid() });

export const adjustmentListQuerySchema = z.object({
  status: z.enum(ADJUSTMENT_STATUSES).optional(),
  branchId: z.string().uuid().optional(),
  search: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AdjustmentListQuery = z.infer<typeof adjustmentListQuerySchema>;

// ---------- Pipeline reports (Phase 7b) ----------
/** Goods on their way to a location: transfers not acknowledged, ships not received */
export const onTheWayQuerySchema = z.object({
  branchId: z.string().uuid().optional(),
  /** Item code or name, transfer or ship number */
  search: z.string().trim().max(100).optional(),
});
export type OnTheWayQuery = z.infer<typeof onTheWayQuerySchema>;

/** Calendar dates, inclusive, in the user's time zone; without them the last 180 days */
const reportDateRange = {
  from: isoDate.optional(),
  to: isoDate.optional(),
};
export const supplierPerformanceQuerySchema = z.object(reportDateRange).refine((v) => !v.from || !v.to || v.from <= v.to, { message: 'From must be on or before to', path: ['to'] });
export type SupplierPerformanceQuery = z.infer<typeof supplierPerformanceQuerySchema>;
export const freightQuerySchema = z
  .object({ ...reportDateRange, supplierId: z.string().uuid().optional() })
  .refine((v) => !v.from || !v.to || v.from <= v.to, { message: 'From must be on or before to', path: ['to'] });
export type FreightQuery = z.infer<typeof freightQuerySchema>;

/** Items running low at a shop, to start a shop order from */
export const branchOrderSuggestionsQuerySchema = z.object({ branchId: z.string().uuid().optional() });
export type BranchOrderSuggestionsQuery = z.infer<typeof branchOrderSuggestionsQuerySchema>;

// ---------- Share pictures (Phase 6) ----------
/** Lines on one share picture */
export const SHARE_ROWS_PER_PAGE = 25;
/** At most this many pictures for one order (500 lines) */
export const SHARE_MAX_PAGES = 20;
export const sharePageSchema = z.object({ page: z.coerce.number().int().min(1).max(SHARE_MAX_PAGES).default(1) });
export type SharePageQuery = z.infer<typeof sharePageSchema>;

// ---------- Notification settings (Phase 5, email in Phase 6) ----------
/**
 * Alerts a user can choose about. `types` are the notification types each one
 * covers. Only `optional` ones can be switched off in the app; the rest always
 * show under the bell. Any of them can also go by email (off by default).
 * In-app defaults depend on who the user is: supplier logins and staff are on,
 * the China team is off (client, 26 Oct: "China notification optional").
 */
export const NOTIFICATION_EVENTS = {
  'branch_order.placed': {
    label: 'A shop places an order',
    description: 'For the warehouse, each supplier on the order (only its own lines) and, if switched on, the China team. Includes the order being cancelled.',
    optional: true,
    types: ['branch_order.placed', 'branch_order.cancelled'],
    defaults: { staff: true, supplier: true, chinaTeam: false },
  },
  'branch_order.declined': {
    label: 'The warehouse declines items on your shop order',
    description: 'With the reason the warehouse gave',
    optional: false,
    types: ['branch_order.declined'],
    defaults: { staff: true, supplier: true, chinaTeam: true },
  },
  'transfer.dispatched': {
    label: 'Goods are on the way to your branch',
    description: 'A delivery to acknowledge, or one that was called off',
    optional: false,
    types: ['transfer.dispatched', 'transfer.cancelled'],
    defaults: { staff: true, supplier: true, chinaTeam: true },
  },
  'transfer.received': {
    label: 'A delivery you sent was received',
    description: 'Including deliveries that arrived short',
    optional: false,
    types: ['transfer.received', 'transfer.short'],
    defaults: { staff: true, supplier: true, chinaTeam: true },
  },
  'china_order.submitted': {
    label: 'A China order is submitted or cancelled',
    description: 'New orders for the China team to confirm',
    optional: false,
    types: ['china_order.submitted', 'china_order.cancelled'],
    defaults: { staff: true, supplier: true, chinaTeam: true },
  },
  'china_order.updated': {
    label: 'The China team updates an order',
    description: 'Confirmed, rejected, quantities or progress changed',
    optional: false,
    types: ['china_order.confirmed', 'china_order.rejected', 'china_order.supplier_edit', 'china_order.progress'],
    defaults: { staff: true, supplier: true, chinaTeam: true },
  },
  'stock_adjustment': {
    label: 'Stock adjustments and counts',
    description: 'One waiting for approval, or yours approved or rejected',
    optional: false,
    types: ['stock_adjustment.requested', 'stock_adjustment.approved', 'stock_adjustment.rejected'],
    defaults: { staff: true, supplier: true, chinaTeam: true },
  },
  'shipment.news': {
    label: 'Shipment news',
    description: 'Goods loaded, an invoice added, the ETA changing, arrival and receipt',
    optional: false,
    types: ['shipment.loaded', 'shipment.invoice', 'shipment.eta', 'shipment.arrived', 'shipment.received', 'china_order.received'],
    defaults: { staff: true, supplier: true, chinaTeam: true },
  },
} as const;
export type NotificationEvent = keyof typeof NOTIFICATION_EVENTS;
export const NOTIFICATION_EVENT_KEYS = Object.keys(NOTIFICATION_EVENTS) as NotificationEvent[];

/** The alert a notification type belongs to, for the user's email choice */
export function notificationEventOf(type: string): NotificationEvent | null {
  for (const k of NOTIFICATION_EVENT_KEYS) if ((NOTIFICATION_EVENTS[k].types as readonly string[]).includes(type)) return k;
  return null;
}

export const notificationSettingSchema = z
  .object({
    event: z.enum(NOTIFICATION_EVENT_KEYS as [NotificationEvent, ...NotificationEvent[]]),
    /** In the app (only for optional alerts) */
    enabled: z.boolean().optional(),
    /** Also by email */
    email: z.boolean().optional(),
  })
  .refine((v) => v.enabled !== undefined || v.email !== undefined, 'Say what to change');
export type NotificationSettingInput = z.infer<typeof notificationSettingSchema>;

// ---------- Warehouse order sheet import ----------
/**
 * Choices made on the preview: the supplier for rows whose item has none,
 * quantities for rows whose quantity couldn't be read (row number → qty, or
 * null to leave the row out), and which ships from the sheet have already
 * arrived (received before I-GOAL, so no stock is added for them).
 */
export const sheetImportOptionsSchema = z.object({
  supplierId: z.string().uuid().optional(),
  quantities: z.record(z.string().regex(/^\d+$/), wholeQty.nullable()).default({}),
  skipRows: z.array(z.number().int().positive()).max(5000).default([]),
  arrivedShips: z.array(z.string().trim().min(1).max(40)).max(2000).optional(),
});
export type SheetImportOptions = z.infer<typeof sheetImportOptionsSchema>;
export const sheetImportStartSchema = z.object({
  uploadId: z.string().uuid(),
  mode: z.enum(['preview', 'commit']),
  options: sheetImportOptionsSchema.default({}),
});
export type SheetImportStartInput = z.infer<typeof sheetImportStartSchema>;

export const invoiceImportOptionsSchema = z.object({
  shipNo: shipNo.optional(),
  containerNo,
  etaDate: isoDate.optional().nullable(),
  shippedOn: isoDate.optional().nullable(),
  invoiceNo: invoiceNo.optional(),
  invoiceDate: isoDate.optional().nullable(),
  supplierId: z.string().uuid().optional(),
  matches: z.record(z.string().regex(/^\d+$/), z.string().uuid().nullable()).default({}),
  /** Remember chosen matches as item aliases, so the next invoice matches by itself */
  saveAliases: z.boolean().default(true),
});
export type InvoiceImportOptions = z.infer<typeof invoiceImportOptionsSchema>;
